import { useEffect, useState } from "react";

import { ANIMATION_TIMINGS } from "../constants";

interface CreditApplicationAnimationState {
  isCreditScoreAnimating: boolean;
  isCreditLineItemVisible: boolean;
  isCreditAnimationComplete: boolean;
}

interface UseCreditApplicationAnimationParams {
  /**
   * Credit amount to add to the score (positive number)
   */
  creditAmount: number;
  /**
   * Score after the initial animation sequence completes
   */
  baseScore: number;
  /**
   * Whether the credit application animation has been triggered (user clicked "Apply")
   */
  isTriggered: boolean;
  /**
   * Called when the full animation sequence completes
   */
  onComplete: () => void;
}

interface UseCreditApplicationAnimationResult {
  /**
   * The target end score for the credit animation
   */
  creditEndScore: number;
  /**
   * Whether the score counter is currently animating the credit
   */
  isCreditScoreAnimating: boolean;
  /**
   * Whether the credit line item should be visible
   */
  isCreditLineItemVisible: boolean;
  /**
   * Label for the progress bar during credit application
   */
  progressBarLabel: string;
}

/**
 * Timer-based animation hook for the credit application sequence.
 * Reuses the same timing constants as useScoreAnimationSequence.
 *
 * Sequence when triggered:
 * 1. Immediately: score animation starts, label = "Applying credit..."
 * 2. After SCORE_ANIMATION_DURATION_MS: score animation ends, label = "Credit applied!"
 * 3. After SCORE_ANIMATION_DURATION_MS + LINE_ITEM_DELAY_MS: line item visible, animation complete
 */
export function useCreditApplicationAnimation(
  params: UseCreditApplicationAnimationParams
): UseCreditApplicationAnimationResult {
  const { creditAmount, baseScore, isTriggered, onComplete } = params;

  const [animationState, setAnimationState] = useState<CreditApplicationAnimationState>({
    isCreditScoreAnimating: false,
    isCreditLineItemVisible: false,
    isCreditAnimationComplete: false,
  });

  const [progressBarLabel, setProgressBarLabel] = useState("");

  useEffect(() => {
    if (!isTriggered) {
      return;
    }

    const timers: NodeJS.Timeout[] = [];

    function scheduleTimer(callback: () => void, delay: number): void {
      const timer = setTimeout(callback, delay);
      timers.push(timer);
    }

    // Step 1: Immediately start score animation
    setAnimationState({
      isCreditScoreAnimating: true,
      isCreditLineItemVisible: false,
      isCreditAnimationComplete: false,
    });
    setProgressBarLabel("Applying credit...");

    // Step 2: Score animation ends
    scheduleTimer(() => {
      setAnimationState((previous) => ({
        ...previous,
        isCreditScoreAnimating: false,
      }));
      setProgressBarLabel("Credit applied!");
    }, ANIMATION_TIMINGS.SCORE_ANIMATION_DURATION_MS);

    // Step 3: Show line item and mark complete
    scheduleTimer(() => {
      setAnimationState((previous) => ({
        ...previous,
        isCreditLineItemVisible: true,
        isCreditAnimationComplete: true,
      }));
      onComplete();
    }, ANIMATION_TIMINGS.SCORE_ANIMATION_DURATION_MS + ANIMATION_TIMINGS.LINE_ITEM_DELAY_MS);

    return () => {
      timers.forEach((timer) => {
        clearTimeout(timer);
      });
    };
  }, [isTriggered, onComplete]);

  const creditEndScore = baseScore + creditAmount;

  return {
    creditEndScore,
    isCreditScoreAnimating: animationState.isCreditScoreAnimating,
    isCreditLineItemVisible: animationState.isCreditLineItemVisible,
    progressBarLabel,
  };
}
